import { createTheme } from '@mui/material/styles'

export const closedDrawerWidth = 50
export const drawerWidth = 260
export const drawerMiniWidth = 80
export const appBarHeight = 60

export const transition = {
  transition: 'all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)',
}

export const containerFluid = {
  paddingRight: 0,
  paddingLeft: 0,
  marginRight: 'auto',
  marginLeft: 'auto',
  width: '100%',
}
declare module '@mui/material/styles' {
  interface Palette {
    takeoutofservice: Palette['primary']
  }

  interface PaletteOptions {
    takeoutofservice?: Palette['primary']
  }
}

// Also extend the Button props to accept the new color
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    takeoutofservice: true
  }
  interface ButtonPropsVariantOverrides {
    mtiAddButton: true
    backButton: true
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    'tab-panel': true
    transparent: true
    'content-panel': true
  }
}

declare module '@mui/material/Box' {
  interface BoxPropsVariantOverrides {
    'page-container': true
    'content-container': true
    'section-container': true
  }
}

declare module '@mui/material/Tooltip' {
  interface TooltipPropsVariantOverrides {
    error: true
    success: true
    info: true
    warning: true
    corporate: true
    white: true
    takeoutofservice: true
  }
}

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#009DE0',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    takeoutofservice: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily:
      "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    h1: {
      fontFamily: "'Source Sans Pro', sans-serif",
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: '1.75rem',
    },
    h2: {
      fontFamily: "'Source Sans Pro', sans-serif",
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: '1.75rem',
    },
    h3: {
      fontFamily: "'Source Sans Pro', sans-serif,Roboto, Helvetica,Arial",
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
    },
    h4: {
      fontFamily: "'Source Sans Pro', sans-serif,Roboto, Helvetica,Arial",
      fontWeight: 400,
      fontSize: '18px',
      lineHeight: '1.375rem',
      marginTop: '10px',
      marginBottom: '10px',
    },
    h5: {
      fontFamily: "'Source Sans Pro', sans-serif",
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    h6: {
      fontFamily: "'Source Sans Pro', sans-serif",
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
    },
    body1: {
      fontFamily:
        "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '15pt',
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#1b3c4a',
          color: 'white',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'rgb(27, 60, 74)',
          fontWeight: 'bold',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'uppercase',
          fontWeight: 500,
          backgroundColor: '#999',
          fontFamily:
            "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontSize: '12px',
          padding: '8px 16px',

          '&.Mui-disabled': {
            opacity: 0.65,
          },
        },

        containedSuccess: {
          backgroundColor: '#4caf50',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#2e7d32',
          },

          '&.MuiButton-containedSuccess.Mui-disabled': {
            backgroundColor: '#4caf50 !important',
            color: '#ffffff !important',
          },
        },
        containedTakeoutofservice: {
          backgroundColor: '#9c27b0',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#7b1fa2',
          },
          '&.Mui-disabled': {
            backgroundColor: '#9c27b0 !important',
            color: '#ffffff !important',
            opacity: '0.65 !important', // Override the global 0.65 opacity
          },
        },
      },

      variants: [
        {
          props: { variant: 'mtiAddButton' },
          style: {
            fontSize: '12px',
            textAlign: 'center',
            padding: '6px 5px 5px 5px',
            minWidth: '36px',
            height: '36px',
            backgroundColor: '#009DE0',
            color: 'white',
            boxSizing: 'border-box',
            '&:hover': {
              backgroundColor: '#0078A0',
            },
          },
        },
        {
          props: { variant: 'backButton' },
          style: {
            backgroundColor: '#999',
            color: '#fff',
            fontWeight: 500,
            textTransform: 'uppercase',
            marginTop: '16px',
            marginRight: '16px',
            padding: '8px 16px',
            width: 'auto',
            height: '100%',
            borderRadius: 4,
            fontSize: '12px',
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            '&.Mui-disabled': {
              opacity: 0.65,
              backgroundColor: '#f5f5f5',
              color: '#757575',
            },

            '&::before': {
              marginRight: '8px',
              fontWeight: 'bold',
            },
          },
        },
      ],
    },
    MuiTooltip: {
      defaultProps: {
        arrow: false,
      },
      styleOverrides: {
        tooltip: {},
        arrow: {},
      },
      variants: [
        {
          props: { variant: 'white' },
          style: {
            backgroundColor: '#fff',
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
            padding: '12px',
            opacity: 1,
            '& .MuiTooltip-arrow': {
              color: '#fff',
              '&::before': {
                backgroundColor: '#fff',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
              },
            },
          },
        },
        {
          props: { variant: 'error' },
          style: {
            backgroundColor: '#d32f2f',
            '& .MuiTooltip-arrow': {
              color: '#d32f2f',
            },
          },
        },
        {
          props: { variant: 'success' },
          style: {
            backgroundColor: '#4caf50',
            '& .MuiTooltip-arrow': {
              color: '#4caf50',
            },
          },
        },
        {
          props: { variant: 'info' },
          style: {
            backgroundColor: '#0288d1',
            '& .MuiTooltip-arrow': {
              color: '#0288d1',
            },
          },
        },
        {
          props: { variant: 'warning' },
          style: {
            backgroundColor: '#f57c00',
            '& .MuiTooltip-arrow': {
              color: '#f57c00',
            },
          },
        },
        {
          props: { variant: 'corporate' },
          style: {
            backgroundColor: '#009DE0',
            color: 'white',
            fontWeight: 500,
            '& .MuiTooltip-arrow': {
              color: '#009DE0',
            },
          },
        },
        {
          props: { variant: 'takeoutofservice' },
          style: {
            backgroundColor: '#9c27b0',
            color: 'white',
            fontWeight: 500,
            '& .MuiTooltip-arrow': {
              color: '#9c27b0',
            },
          },
        },
      ],
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: '2px',
          margin: '0',
          backgroundColor: 'transparent',
        },
        horizontal: {
          display: 'flex',
          justifyContent: 'space-between',
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          padding: '0 16px',
        },
        horizontal: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          '& .MuiStepIcon-root': {
            color: 'rgba(0, 0, 0, 0.38)',
            width: '24px',
            height: '24px',

            '& text': {
              fill: '#fff',
              fontSize: '14px',
              fontWeight: 500,
            },

            '&.Mui-completed': {
              color: '#3f51b5 !important', // Same blue color
              '& text': {
                fill: 'white',
              },
            },

            // Active state (current step)
            '&.Mui-active': {
              color: '#3f51b5 !important', // Same blue color as completed
              '& text': {
                fill: 'white',
              },
            },

            // Make sure active completed steps also use the same color
            '&.Mui-active.Mui-completed': {
              color: '#3f51b5 !important',
              '& text': {
                fill: 'white',
              },
            },

            marginTop: '0',
          },
        }, // Removed incorrect label, iconContainer nesting
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          marginTop: 0,
          '&.Mui-active': {
            '& .MuiStepConnector-line': {
              borderColor: '#3f51b5',
            },
          },
          '&.Mui-completed': {
            '& .MuiStepConnector-line': {
              borderColor: '#4CAF50',
            },
          },
        },
        line: {
          borderColor: '#E0E0E0',
          borderTopWidth: '1px',
        },
        alternativeLabel: {
          left: 'calc(-50% + 16px)',
          right: 'calc(50% + 16px)',
        },
      },
    },

    MuiBox: {
      defaultProps: {},
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',

          '&.datagrid-container': {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            borderRadius: '4px',
            overflow: 'hidden',
          },
        },
      },
      variants: [
        {
          props: { variant: 'page-container' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: 'transparent',
            padding: '24px',
            minHeight: 'calc(100vh - 64px)',
          },
        },
        {
          props: { variant: 'content-container' },
          style: {
            backgroundColor: 'transparent',
            marginBottom: '24px',
            width: '100%',
          },
        },
        {
          props: { variant: 'section-container' },
          style: {
            backgroundColor: 'transparent',
            marginBottom: '16px',
            width: '100%',
          },
        },
      ],
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
      // Fix for variants not being properly nested
      variants: [
        {
          props: { variant: 'tab-panel' },
          style: {
            backgroundColor: '#ffffff',
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: 16,
            marginTop: 8,
          },
        },
        {
          props: { variant: 'transparent' },
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        {
          props: { variant: 'content-panel' },
          style: {
            backgroundColor: '#ffffff',
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: 16,
          },
        },
      ],
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '48px',
          backgroundColor: 'transparent',
          '& .MuiTabs-flexContainer': {
            display: 'flex',
            justifyContent: 'center',
          },
          '& .MuiTabs-scroller': {
            width: `calc(100% - 100px)`,
          },
        },
        indicator: {
          backgroundColor: '#000',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 400,
          textTransform: 'uppercase',

          color: 'rgba(0, 0, 0, 0.87)',
          '&.Mui-selected': {
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: 400,
          },
          minWidth: 120,
          padding: '6px 12px',
          textAlign: 'center',
          flex: 1,
        },
      },
    },

    MuiTabPanel: {
      styleOverrides: {
        root: {
          margin: '0',
          padding: '0',
          backgroundColor: '#fff',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          width: '100%',
          border: 'none',
          borderRadius: '4px',
          boxShadow: 'none',
          backgroundColor: '#fff',
          fontFamily:
            "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          height: 'auto !important', // Force auto height
          maxHeight: 'none !important', // Remove max height constraint

          '& .MuiDataGrid-iconSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid #e0e0e0',
            borderTop: 'none',
            backgroundColor: '#fff',
            minHeight: '48px',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            padding: '0 16px',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.875rem',
            overflow: 'visible',
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textShadow: '0 0 0.1px rgba(0, 0, 0, 0.1)',
            borderRight: 'none',
            padding: '6px 10px 6px 10px',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            display: 'flex',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: 'auto !important',
            lineHeight: '1.5',
            '& > div': {
              maxWidth: '100%',
              whiteSpace: 'normal',
            },
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
            justifyContent: 'center',
            '& .MuiCheckbox-root.Mui-checked': {
              color: '#f50057',
            },
          },
          '& .MuiDataGrid-columnHeaderCheckbox': {
            width: '50px !important',
            minWidth: '50px !important',
            padding: '0',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeaderWrapper': {
            '& .MuiDataGrid-columnHeader': {
              width: 'auto !important',
            },
          },
          '& .MuiDataGrid-row': {
            '&.Mui-selected': {
              backgroundColor: 'rgba(63, 81, 181, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(63, 81, 181, 0.12)',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            padding: '0',
            minHeight: '56px',
          },
          '& .MuiTablePagination-root': {
            color: 'rgba(0, 0, 0, 0.87)',
            fontFamily: 'inherit',
            '& .MuiDataGrid-overlay': {
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '16px',
            },
            '& .MuiDataGrid-overlayContent': {
              position: 'static',
              color: 'rgba(0, 0, 0, 0.54)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              '& span': {
                display: 'none',
              },
              '&::after': {
                content: '"No records found"',
                display: 'block',
              },
            },

            '& .MuiTablePagination-select': {
              backgroundColor: '#fff',
            },
            '& .MuiTablePagination-selectIcon': {
              color: 'rgba(0, 0, 0, 0.54)',
            },
          },
          '& .MuiTableSortLabel-icon': {
            color: 'rgba(0, 0, 0, 0.54) !important',
            opacity: 1,
          },
          '& .MuiDataGrid-main': {
            overflow: 'hidden',
            width: '100%',
          },
          '& .MuiMenu-paper, & .MuiPopover-paper': {
            backgroundColor: '#ffffff !important',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          },
          '& .MuiMenuItem-root': {
            backgroundColor: '#ffffff',
            fontFamily: 'inherit',
            color: 'rgba(0, 0, 0, 0.87)',
            opacity: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '& .MuiDataGrid-cell': {
              backgroundColor: 'transparent',
            },
          },
          '& .MuiDataGrid-menu, & .MuiDataGrid-panel, & .MuiDataGrid-gridMenuList':
            {
              backgroundColor: '#ffffff',
              opacity: 1,
              color: 'rgba(0, 0, 0, 0.87)',
            },
          '& .MuiDataGrid-toolbarContainer': {
            padding: '0',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            height: '54px',
            '& .MuiButton-root': {
              color: 'rgba(0, 0, 0, 0.87)',
              fontFamily: 'inherit',
            },
            '& .search-container': {
              display: 'flex',
              alignItems: 'center',
              margin: '8px 16px',
              width: '15%',
              minWidth: '200px',
              maxWidth: '300px',
              position: 'relative',
              '& .MuiFormControl-root': {
                margin: '8px 16px',
                width: '15%',
                minWidth: '200px',
                maxWidth: '300px', // Maximum width
                '& .MuiInput-root': {
                  '&::before': {
                    marginLeft: '16px',
                    marginRight: '16px',
                    width: 'calc(100% - 32px)',
                    borderColor: 'rgba(0, 0, 0, 0.2)',
                  },
                  '&::after': {
                    marginLeft: '16px',
                    marginRight: '16px',
                    width: 'calc(100% - 32px)',
                    borderColor: '#009DE0',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderColor: 'rgba(0, 0, 0, 0.6)',
                  },
                  '&.Mui-focused::after': {
                    transform: 'scaleX(1)',
                  },
                },
                '& .MuiInputBase-input': {
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
                '& .MuiInputBase-root': {
                  '&.Mui-focused': {
                    backgroundColor: 'transparent',
                  },
                },
              },

              '& .clear-search-button': {
                position: 'absolute',
                right: '8px',
                zIndex: 1,
                padding: '4px',

                '& .MuiIconButton-root': {
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                },
              },
            },
          },
          '& .MuiFormControl-root:not(.search-container .MuiFormControl-root)':
            {
              margin: '8px 16px',
              width: '15%',
              minWidth: '200px',
              maxWidth: '300px',
              '& .MuiInput-root': {
                '&::before': {
                  marginLeft: '16px',
                  marginRight: '16px',
                  width: 'calc(100% - 32px)',
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                },
                '&::after': {
                  marginLeft: '16px',
                  marginRight: '16px',
                  width: 'calc(100% - 32px)',
                  borderColor: '#009DE0',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderColor: 'rgba(0, 0, 0, 0.6)',
                },
              },
              '& .MuiInputBase-input': {
                paddingLeft: '8px',
                paddingRight: '8px',
              },
              '& .MuiInputBase-root': {
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
              },
            },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          // Default state (placeholder) - black color
          color: 'rgba(0, 0, 0, 0.6)',
          fontFamily:
            "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",

          // When focused - blue color (Image 3)
          '&.Mui-focused': {
            color: '#1976d2 !important', // Blue when focused
          },

          // When has error - red color (Image 2)
          '&.Mui-error': {
            color: '#d32f2f !important', // Red when error
          },

          // When error AND focused - keep red (not blue)
          '&.Mui-error.Mui-focused': {
            color: '#d32f2f !important', // Stay red when error and focused
          },

          // When label is shrunk but not focused and no error - black (Image 4)
          '&.MuiInputLabel-shrunk:not(.Mui-focused):not(.Mui-error)': {
            color: 'rgba(0, 0, 0, 0.6) !important', // Black when filled
          },
          '&.search-label': {
            color: 'rgba(0, 0, 0, 0.6)',
            fontFamily:
              "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontSize: '1rem',

            '&.Mui-focused': {
              color: '#009DE0 !important',
            },

            '&.MuiInputLabel-shrunk': {
              transform: 'translate(0, -1.5px) scale(0.75)',
              transformOrigin: 'top left',
            },
          },
        },
        outlined: {
          // Specific styling for outlined variant
          color: 'rgba(0, 0, 0, 0.6)',

          '&.MuiInputLabel-shrunk': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },

          // Focused state - blue
          '&.Mui-focused': {
            color: '#1976d2 !important',
          },

          // Error state - red
          '&.Mui-error': {
            color: '#d32f2f !important',
          },

          // Error and focused - stay red
          '&.Mui-error.Mui-focused': {
            color: '#d32f2f !important',
          },
        },
      },
    },

    // Also add MuiFormHelperText for the error message styling
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily:
            "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontSize: '0.75rem',
          marginTop: '4px',

          '&.Mui-error': {
            color: '#d32f2f', // Red error message
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'inherit',
          '&.MuiDataGrid-cellSelect': {
            '& .MuiSelect-select': {
              backgroundColor: '#fff',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              padding: '6px 8px',

              '&:focus': {
                backgroundColor: '#fff',
                outline: 'none',
              },
            },
          },
          '&.MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23)', // Default border color
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.87)', // Hover border color
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2 !important', // Blue border when focused
              borderWidth: '2px',
            },
            '&.Mui-error fieldset': {
              borderColor: '#d32f2f !important', // Red border when error
            },
            '&.Mui-error.Mui-focused fieldset': {
              borderColor: '#d32f2f !important', // Stay red when error and focused
              borderWidth: '2px',
            },
          },
          // Add styles for UpdateSelect component (keeping your existing styles)
          '&.updateSelect': {
            backgroundColor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
              boxShadow: 'none !important',
            },
            '& fieldset': {
              border: 'none !important',
            },
            '& .MuiSelect-select': {
              padding: 0,
              margin: 0,
              boxShadow: 'none',
              border: 'none !important',
            },
            fontFamily: 'inherit',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              transition: 'background-color 0.2s ease',
            },
            '&:hover:not(.MuiDisabled)::after': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
            '&.MuiDisabled::after': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
            height: '20px',
            padding: '0',
            margin: '0',
            borderRadius: 0,
            outline: 'none !important',
            borderColor: 'transparent !important',
            boxShadow: 'none !important',
          },
        },
        icon: {
          color: 'rgba(0, 0, 0, 0.54)',
          right: 4,
        },
        nativeInput: {
          fontFamily: 'inherit',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          backgroundColor: '#fff',
          opacity: 1,
        },
        list: {
          padding: '4px 0',
          backgroundColor: '#fff',
          opacity: 1,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          opacity: 1,
          fontFamily: 'inherit',
          fontSize: '0.875rem',
          backgroundColor: '#fff',
          minHeight: 36,
          textAlign: 'left',
          justifyContent: 'flex-start',
          '&.Mui-selected': {
            backgroundColor: 'rgba(63, 81, 181, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(63, 81, 181, 0.12)',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#FF5733',
          color: 'white',
        },
      },
    },
  },
})

export const layoutStyles = {
  wrapper: {
    position: 'relative',
    top: 0,

    height: '100vh',
    backgroundColor: '#eee',

    '&:after': {
      display: 'table',
      clear: 'both',
      content: '" "',
    },
  },

  mainPanel: {
    transitionProperty: 'top, bottom, width, left, right',
    transitionDuration: '.33s',
    transitionTimingFunction: 'cubic-bezier(0.685, 0.0473, 0.346, 1)',
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
    },
    overflow: 'auto',
    position: 'relative',
    float: 'right',
    ...transition,

    width: '100%',
    overflowScrolling: 'auto',
    height: '100vh',

    backgroundColor: '#eee',
    padding: 0,
    margin: 0,
  },

  mainPanelSidebarMini: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerMiniWidth}px)`,
    },
  },

  mainPanelSidebarClosed: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${closedDrawerWidth}px)`,
    },
  },

  mainPanelWithPerfectScrollbar: {
    overflow: 'hidden !important',
  },

  content: {
    padding: '10px 15px',
    minHeight: 'calc(100vh - 123px)',
  },

  container: {
    ...containerFluid,
  },

  map: {
    marginTop: '70px',
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: 'none',
    backgroundColor: theme.palette.background.paper,
    height: appBarHeight,
    width: '100%',
    position: 'relative',
    marginTop: '5px',
    zIndex: theme.zIndex.appBar,
    transitionProperty: 'all',
    transitionDuration: '.33s',
    transitionTimingFunction: 'cubic-bezier(0.685, 0.0473, 0.346, 1)',
  },

  menuButton: {
    marginRight: theme.spacing(2),
  },

  title: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontSize: '1.1rem',
  },

  appContainer: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: 'transparent',
  },
  pageContainer: {
    backgroundColor: 'transparent',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  tabsContainer: {
    backgroundColor: 'transparent',
    paddingLeft: '15px',
    paddingRight: '15px',
    marginBottom: theme.spacing(2),

    width: '100%',
  },

  tabPanel: {
    backgroundColor: '#fff',

    margin: '0',

    width: '100%',
  },
  contentPanel: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    padding: theme.spacing(0),
  },
  section: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  logoNormal: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: '1.5em',
    transition: 'all 300ms linear',
    display: 'block',
    opacity: '1',
    transform: 'translate3d(0px, 0, 0)',
    textTransform: 'uppercase',
    padding: '5px 0px',
    fontSize: '18px',
    fontWeight: 400,
    overflow: 'hidden',
    '&,&:hover,&:focus': {
      color: 'inherit',
    },
    width: '140px',
    marginLeft: '10px',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexEnd: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}
export const fieldLabelSx = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: '1',
  letterSpacing: 'unset',
  color: '#AAAAAA',
  display: 'block',
  marginBottom: '1px',
  '&.Mui-error': { color: '#AAAAAA !important' },
  '&.Mui-focused': { color: '#AAAAAA !important' },
  '&.MuiFormLabel-colorPrimary': { color: '#AAAAAA' },
  '&.MuiFormLabel-colorPrimary.Mui-focused': { color: '#AAAAAA' },
  '&.MuiFormLabel-filled': { color: '#AAAAAA' },
} as const

export const labelRoot = fieldLabelSx
export const formLegendSx = fieldLabelSx
export const radioGroupLabelSx = fieldLabelSx
export const labelNoErrorColor = {
  '&.Mui-error': { color: '#AAAAAA !important' },
} as const

export const requiredIndicator = {
  '&::after': {
    content: '" *"',
    color: '#f44336',
  },
} as const

// fieldLabelSx sits tight against its control, which works for inputs that
// carry their own padding but not for a plain control such as a button.
export const fieldLabelSpacedSx = {
  ...fieldLabelSx,
  lineHeight: 1.4,
  marginBottom: '8px',
} as const

// Action button placed inside a form column, used to push something to a
// device rather than to navigate. Rounded so it reads as an inline action
// next to the squared form and wizard buttons.
export const formActionButtonSx = {
  alignSelf: 'flex-start',
  borderRadius: '999px',
  minWidth: '220px',
  minHeight: '36px',
  paddingInline: '20px',
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#7b1fa2',
  },
  '&.Mui-disabled': {
    backgroundColor: '#9c27b0',
    color: '#ffffff',
    opacity: 0.65,
  },
} as const

// Applied on top of formActionButtonSx once the action has succeeded: the
// button stays disabled but must read as done rather than as unavailable.
export const formActionButtonDoneSx = {
  '&.Mui-disabled': {
    backgroundColor: '#4caf50',
    color: '#ffffff',
    opacity: 1,
  },
} as const

export const radioSx = {
  color: 'rgba(0, 0, 0, 0.54)',
  '&.Mui-checked': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
} as const

export const checkboxSx = {
  color: 'rgba(0, 0, 0, 0.54)',
  '&.Mui-checked': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
} as const

export const requiredCheckboxSx = (hasError: boolean) => ({
  color: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.54)',
  '&.Mui-checked': {
    color: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
  },
})

export const requiredCheckboxGroupItemSx = {
  display: 'block',
  mb: 1,
  mt: 1,
} as const

export const requiredCheckboxLabelTextSx = {
  lineHeight: 1,
} as const

export const selectStandardSx = {
  '&:before': { borderBottomColor: '#D2D2D2' },
  '&:after': { borderBottomColor: '#D2D2D2' },
} as const

export const calibrationCard = {
  display: 'inline-block',
  position: 'relative',
  width: '100%',
  margin: '25px 0',
  boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.14)',
  borderRadius: '6px',
  color: 'rgba(0, 0, 0, 0.87)',
  background: '#fff',
  overflow: 'visible',
} as const

export const warningCardHeader = {
  margin: '-20px 15px 0',
  borderRadius: '3px',
  padding: '15px',
  background: 'linear-gradient(60deg, #ffa726, #fb8c00)',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px rgba(255,152,0,.4)',
  display: 'inline-block',
} as const

export const calibrationCardActions = {
  margin: '0 0 10px',
  paddingTop: '10px',
  borderTop: '1px solid #eee',
  height: 'auto',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 300,
  lineHeight: '1.5em',
  display: 'flex',
  gap: 1,
} as const

export default theme
